import React, { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./components/ThemeProvider";
import { ProtectedRoute } from "./components/layout/ProtectedRoute";
import { SidebarProvider, SidebarTrigger, SidebarInset } from "@/components/ui/sidebar";
import { AppSidebar } from "./components/layout/AppSidebar";
import { Loader2 } from "lucide-react";
import { useSystemLock } from "./hooks/useSystemLock";
import { SystemLockScreen } from "./components/SystemLockScreen";

// Auth is not lazy-loaded as it's the initial page
import Auth from "./pages/Auth";

// Lazy load all other pages for code splitting
const Index = lazy(() => import("./pages/Index"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const DividaAtiva = lazy(() => import("./pages/DividaAtiva"));
const Acordos = lazy(() => import("./pages/Acordos"));
const Notificacoes = lazy(() => import("./pages/Notificacoes"));
const Relatorios = lazy(() => import("./pages/Relatorios"));
const RelatorioProdutividadeAgente = lazy(() => import("./pages/RelatorioProdutividadeAgente"));
const Usuarios = lazy(() => import("./pages/Usuarios"));
const ImportarDados = lazy(() => import("./pages/ImportarDados"));
const HistoricoImportacoes = lazy(() => import("./pages/HistoricoImportacoes"));

const Estrategias = lazy(() => import("./pages/Estrategias"));
const Configuracoes = lazy(() => import("./pages/Configuracoes"));
const Contribuinte = lazy(() => import("./pages/Contribuinte"));
const Rating = lazy(() => import("./pages/Rating"));
const Protesto = lazy(() => import("./pages/Protesto"));
const AcoesMassa = lazy(() => import("./pages/AcoesMassa"));
const Backoffice = lazy(() => import("./pages/Backoffice"));
const DocumentacaoER = lazy(() => import("./pages/DocumentacaoER"));
const AuditoriaLogins = lazy(() => import("./pages/AuditoriaLogins"));

const NotFound = lazy(() => import("./pages/NotFound"));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const queryClient = new QueryClient();

const LockGate = ({ children }: { children: React.ReactNode }) => {
  const { effectiveLocked, expired, licensed_until, message, isOwner, loading, auto_lock_enabled, daysRemaining } =
    useSystemLock();
  if (loading) return <PageLoader />;
  if (effectiveLocked && !isOwner) {
    const suffix =
      expired && licensed_until
        ? `\n\nLicença vencida em ${new Date(`${licensed_until}T00:00:00`).toLocaleDateString('pt-BR')}.`
        : '';
    return <SystemLockScreen message={`${message}${suffix}`} />;
  }
  const showWarning =
    isOwner && auto_lock_enabled && daysRemaining !== null && daysRemaining <= 7;
  return (
    <>
      {showWarning && (
        <div className="w-full bg-destructive/10 text-destructive text-xs text-center py-2 px-4">
          {daysRemaining! < 0
            ? `Licença vencida em ${new Date(`${licensed_until}T00:00:00`).toLocaleDateString('pt-BR')} — o sistema está bloqueado para os demais usuários.`
            : `Licença vence em ${daysRemaining} dia(s) (${new Date(`${licensed_until}T00:00:00`).toLocaleDateString('pt-BR')}). Aprove a renovação em Configurações.`}
        </div>
      )}
      {children}
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <LockGate>
                  <SidebarProvider>
                    <div className="flex min-h-screen w-full">
                      <AppSidebar />
                      <SidebarInset>
                        <header className="h-14 border-b flex items-center px-4 bg-card">
                          <SidebarTrigger />
                        </header>
                        <div className="flex-1 overflow-auto bg-background">
                          <Suspense fallback={<PageLoader />}>
                            <Routes>
                              <Route path="/" element={<Index />} />
                              <Route
                                path="/dashboard"
                                element={
                                  <ProtectedRoute requireAdmin>
                                    <Dashboard />
                                  </ProtectedRoute>
                                }
                              />
                              <Route path="/divida-ativa" element={<DividaAtiva />} />
                              <Route path="/acordos" element={<Acordos />} />
                              <Route
                                path="/backoffice"
                                element={
                                  <ProtectedRoute requireBackoffice>
                                    <Backoffice />
                                  </ProtectedRoute>
                                }
                              />
                              <Route path="/contribuinte" element={<Contribuinte />} />
                              <Route path="/notificacoes" element={<Notificacoes />} />
                              <Route path="/relatorios" element={<Relatorios />} />
                              <Route path="/relatorios/produtividade-agente" element={<RelatorioProdutividadeAgente />} />
                              <Route
                                path="/usuarios"
                                element={
                                  <ProtectedRoute requireAdmin>
                                    <Usuarios />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/estrategias"
                                element={
                                  <ProtectedRoute requireAdmin>
                                    <Estrategias />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/importar-dados"
                                element={
                                  <ProtectedRoute requireAdmin>
                                    <ImportarDados />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/historico-importacoes"
                                element={
                                  <ProtectedRoute requireAdmin>
                                    <HistoricoImportacoes />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/rating"
                                element={
                                  <ProtectedRoute requireAdmin>
                                    <Rating />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/protesto"
                                element={
                                  <ProtectedRoute requireAdmin>
                                    <Protesto />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/acoes-massa"
                                element={
                                  <ProtectedRoute requireAdmin>
                                    <AcoesMassa />
                                  </ProtectedRoute>
                                }
                              />
                              <Route path="/configuracoes" element={<Configuracoes />} />
                              <Route path="/documentacao-er" element={<DocumentacaoER />} />
                              <Route
                                path="/auditoria-logins"
                                element={
                                  <ProtectedRoute requireAdmin>
                                    <AuditoriaLogins />
                                  </ProtectedRoute>
                                }
                              />
                              <Route path="*" element={<NotFound />} />
                            </Routes>
                          </Suspense>
                        </div>
                      </SidebarInset>
                    </div>
                  </SidebarProvider>
                  </LockGate>
                </ProtectedRoute>
              }
            />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
